import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Box,
} from "@mui/material";
import { FormsIds, ScanDocumentType } from "../../Utils/Constants";
import { Vocabulary } from "../../Utils/Vocabulary";
import { IAddDocumentModalProps } from "../../Utils/Types";
import { DocumentsModel } from "../../Utils/Models";
import DropzoneComponent from "../DropzoneComponent";
import DocumentTypeAutocomplete from "./DocumentTypeAutocomplete";
import { useState, useEffect } from "react";
import { postData } from "../../Services/postData";
import { endpoints } from "../../Utils/UrlEnum";
import { updateData } from "../../Services/updateData";
import { checkAndUpdateOrderStatusOnDocumentUpload } from "../../Utils/DocumentUtils";
import ScanId from "../ScanIdModal";
import { blobToFile } from "../../Utils/ProcessImages";
import { fetchAllDocumentTypes, isValidDate } from "../../Utils/Utils";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { getFromCacheOrFetch } from "../../Utils/getFromCacheOrFetch";

const validationSchema = yup.object({
  documents: yup.array().min(1, Vocabulary.requiredField),
  expirationDate: yup.date().nullable(),
  documentTypeId: yup.string().nullable(),
  isTemplate: yup.boolean().default(false),
});

export default function AddDocumentModal({
  onClose,
  onSave,
  formRef,
  editData,
  clientId,
  orderId,
  requiredDocumentTypes,
  existingDocuments,
  allStatuses,
  showManageTypesButton = true,
}: IAddDocumentModalProps) {
  const [documentFiles, setDocumentFiles] = useState<any[]>([]);
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeIsTemplate, setNewTypeIsTemplate] = useState(false);
  const [allDocumentTypes, setAllDocumentTypes] = useState<any[]>([]);
  const [selectedDocumentTypeSlug, setSelectedDocumentTypeSlug] = useState<string | null>(null);
  const [scanIdModal, setScanIdModal] = useState(false);
  const [scannedCategories, setScannedCategories] = useState<any[]>([]);
  const memo = useCustomMemo();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<DocumentsModel>({
    resolver: yupResolver(validationSchema) as any,
    defaultValues: editData || new DocumentsModel(),
  });

  const documentTypeId = watch("documentTypeId");

  // Fetch all document types from cache
  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<any[]>({
      cacheKey: "allDocumentTypes",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchAllDocumentTypes(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setAllDocumentTypes(data);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Track selected document type slug
  useEffect(() => {
    const selectedType = allDocumentTypes.find(
      (type) => type._id === documentTypeId
    );
    setSelectedDocumentTypeSlug(selectedType?.slug || null);
  }, [documentTypeId, allDocumentTypes]);

  useEffect(() => {
    if (editData) {
      reset({
        ...editData,
        documentTypeId: (editData.documentTypeId as any)?._id,
      });

      if (editData?.document) setDocumentFiles(["/" + editData?.document]);
    }
  }, [editData, reset]);

  const handleAddNewType = async () => {
    if (!newTypeName.trim()) return;

    try {
      const response = await postData(endpoints.documentTypes, {
        name: newTypeName.trim(),
        isTemplate: newTypeIsTemplate,
      });
      if (response?.data?._id && !newTypeIsTemplate) {
        setValue("documentTypeId", response.data._id);
      }
      setNewTypeName("");
      setNewTypeIsTemplate(false);
      setOpenTypeDialog(false);
    } catch (error) {
      console.error("Error adding document type:", error);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const formData = new FormData();

      // --- Fields mapping ---
      const appendIf = (key: string, value?: unknown) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, String(value));
        }
      };

      appendIf("documentTypeId", data.documentTypeId);
      appendIf("_id", data._id);
      appendIf("clientId", clientId);
      appendIf("orderId", orderId);

      if (data.expirationDate) {
        const date =
          data.expirationDate instanceof Date
            ? data.expirationDate
            : new Date(data.expirationDate);
        formData.append("expirationDate", date.toISOString());
      }

      if (typeof data.isTemplate === "boolean") {
        appendIf("isTemplate", data.isTemplate);
      }

      documentFiles?.forEach((file) => formData.append("documents", file));

      // --- API call ---
      const isUpdate = Boolean(data._id);
      const url = isUpdate
        ? `${endpoints.document}/${data._id}`
        : endpoints.document;

      const apiCall = isUpdate ? updateData : postData;
      const res = await apiCall(url, formData);

      if (!res) {
        console.error("Error response:", res);
        return;
      }

      // Update client categories if scanned
      if (clientId && scannedCategories.length > 0) {
        try {
          await updateData(`${endpoints.client.main}/${clientId}`, {
            categories: scannedCategories,
          });
        } catch (error) {
          console.error("Error updating client categories:", error);
        }
      }

      onSave?.(res.data || data);

      // Check if this completes all required documents and update order status
      // Must happen AFTER document save and BEFORE close to ensure UI refresh picks up new status
      if (orderId && allStatuses) {
        await checkAndUpdateOrderStatusOnDocumentUpload(
          orderId,
          data.documentTypeId,
          requiredDocumentTypes || [],
          existingDocuments || [],
          allStatuses
        );
      }

      onClose();
    } catch (error) {
      console.error("Error saving document:", error);
    }
  };

  /**
   * Handles the file upload event from the Dropzone component.
   * @param {any[]} files - The files selected by the user.
   * @description
   * This function is called when the user selects a file from the Dropzone component.
   * If the user selects a file, it sets the value of the documents field in the model.
   */
  const handleFileUpload = (files: any[]) => {
    setDocumentFiles(files);
    setValue("documents", files);
  };

  const mergeImages = (imagePaths: string[]): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (imagePaths.length === 0) {
        reject(new Error("No images to merge"));
        return;
      }

      if (imagePaths.length === 1) {
        resolve(imagePaths[0]);
        return;
      }

      // Load all images
      const imageElements = imagePaths.map((path) => {
        const img = new Image();
        img.src = path;
        return img;
      });

      // Wait for all images to load
      Promise.all(
        imageElements.map(
          (img) =>
            new Promise<void>((resolveImg) => {
              img.onload = () => resolveImg();
            })
        )
      ).then(() => {
        // Calculate total height and max width for vertical stacking
        const totalHeight = imageElements.reduce((sum, img) => sum + img.height, 0);
        const maxWidth = Math.max(...imageElements.map((img) => img.width));

        // Create canvas
        const canvas = document.createElement("canvas");
        canvas.width = maxWidth;
        canvas.height = totalHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        // Draw images vertically
        let currentY = 0;
        imageElements.forEach((img) => {
          ctx.drawImage(img, 0, currentY, img.width, img.height);
          currentY += img.height;
        });

        // Convert to base64
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      }).catch(reject);
    });
  };

  const processScanCompleted = async (data: any) => {
    setScanIdModal(false);

    // Extract expiration date from result
    const expiringDate = data?.result?.expiringDate;
    if (expiringDate && isValidDate(expiringDate)) {
      try {
        // Parse Romanian date format (DD.MM.YYYY)
        const [day, month, year] = expiringDate.split(".");
        const parsedDate = new Date(`${year}-${month}-${day}`);
        
        if (!isNaN(parsedDate.getTime())) {
          setValue("expirationDate", parsedDate);
        }
      } catch (error) {
        console.error("Error parsing expiration date:", error);
      }
    }

    // Update client categories
    if (data?.result?.categories && Array.isArray(data.result.categories)) {
      setScannedCategories(data.result.categories);
    }

    // Get all base64 images
    const images = data?.image || [];
    const imagePaths = images
      .filter((img: any) => img?.path?.startsWith("data:"))
      .map((img: any) => img.path);

    if (imagePaths.length > 0) {
      try {
        // Merge all images into a single image
        const mergedImagePath = await mergeImages(imagePaths);
        
        // Convert merged image to File
        const file = blobToFile(mergedImagePath);
        setDocumentFiles([file]);
        setValue("documents", [file]);
      } catch (error) {
        console.error("Error merging and converting images:", error);
      }
    }
  };

  return (
    <form
      id={FormsIds.documentsForm}
      onSubmit={handleSubmit(onSubmit)}
      ref={formRef}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DocumentTypeAutocomplete
            control={control}
            name="documentTypeId"
            error={errors.documentTypeId}
            showManageButton={showManageTypesButton}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="expirationDate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="date"
                label={Vocabulary.expirationDocumentDate}
                InputLabelProps={{ shrink: true }}
                size="small"
                value={
                  field.value
                    ? new Date(field.value).toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  field.onChange(
                    e.target.value ? new Date(e.target.value) : null
                  )
                }
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          {selectedDocumentTypeSlug !== "permis" && (
            <DropzoneComponent
              maxFiles={10}
              multiple={true}
              onSave={handleFileUpload}
              isPreview={true}
              paths={documentFiles}
              acceptedFileTypes={{
                "application/pdf": [".pdf"],
                "image/*": [".png", ".jpg", ".jpeg"],
                "application/msword": [".doc"],
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                  [".docx"],
              }}
              error={errors.documents}
            />
          )}
        </Grid>

        {selectedDocumentTypeSlug === "permis" && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setScanIdModal(true)}
              startIcon={<span style={{ fontSize: "1.5rem" }}>📷</span>}
            >
              {Vocabulary.scanDriverLicense || "Scaneaza Permisul de Conducere"}
            </Button>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={openTypeDialog}
        onClose={() => setOpenTypeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{Vocabulary.addNewDocumentType}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={Vocabulary.documentTypeName}
            fullWidth
            variant="outlined"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddNewType();
              }
            }}
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={newTypeIsTemplate}
                  onChange={(e) => setNewTypeIsTemplate(e.target.checked)}
                  color="primary"
                />
              }
              label={Vocabulary.template}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTypeDialog(false)}>
            {Vocabulary.cancel}
          </Button>
          <Button
            onClick={handleAddNewType}
            variant="contained"
            disabled={!newTypeName.trim()}
          >
            {Vocabulary.add}
          </Button>
        </DialogActions>
      </Dialog>

      <ScanId
        openModal={scanIdModal}
        handleClose={() => setScanIdModal(false)}
        doneProcess={processScanCompleted}
        documentType={selectedDocumentTypeSlug === "permis" ? ScanDocumentType.DRIVER_LICENSE : ScanDocumentType.ID}
      />
    </form>
  );
}
